/**
 * User Status Service
 * 
 * Fetches user status and reward limits from the real admin API endpoint.
 * Returns user verification info, partner details, point balance, and reward limits.
 */

import type { UserStatusResponse, RewardLimitStatus, UserRewardLimitsResponse } from '@/app/types/user';
import { api } from '@/app/services/apiClient';
import { logger } from '@/app/utils/logger';

/**
 * User Status Service Interface
 * Define the contract that any implementation must follow
 */
export interface IUserStatusService {
  getUserStatus(userId: number): Promise<UserStatusResponse>;
  getUserRewardLimits(userId: number): Promise<RewardLimitStatus[]>;
}

/**
 * Real API Implementation
 * Calls GET /api/v1/users/{user_id}/status and /reward-limits
 */
const apiUserStatusService: IUserStatusService = {
  async getUserStatus(userId: number): Promise<UserStatusResponse> {
    const response = await api.get<UserStatusResponse>(
      `/api/v1/users/${userId}/status`
    );
    return response.data;
  },

  async getUserRewardLimits(userId: number): Promise<RewardLimitStatus[]> {
    try {
      const response = await api.get<UserRewardLimitsResponse>(
        `/api/v1/users/${userId}/reward-limits`
      );
      return response.data.limits;
    } catch (err) {
      // Log but don't fail - limits are optional enhancement
      logger.warn('Failed to fetch user reward limits', { userId, error: err });
      return [];
    }
  },
};

// Export the real API implementation
export const userStatusService: IUserStatusService = apiUserStatusService;

// Also export as default for easier imports
export default userStatusService;
